import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { MethodTypeRepository } from '@telespot/domain';
import { ParseMethodTypeRepository } from '@telespot/infrastructure/parse';
import { ListAssignedPipelinesQuery } from '@protocols/use-cases';

import { AssignedPipelinesEffects, LIST_ASSIGNED_PIPELINES_QUERY } from '../../../effects/assigned-pipelines.effects';
import { pipelineStoreProviders } from '../../../providers/store.providers';
import { pipelineEditorSessionProviders } from '../pipeline-editor/pipeline-editor.facade.providers';

import { MethodPipelineManagerFacade } from './method-pipeline-manager.facade';

const MANAGER_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>('MANAGER_METHOD_TYPE_REPOSITORY');

/**
 * Providers for one pipeline manager instance. Spreads the pipeline-catalog stack
 * (left list), the assigned-pipelines effects/query (right list), the facade, and
 * the pipeline-editor SESSION tier the dialog resolves against.
 *
 * NOT self-resolvable. The host must provide, per mount or above:
 * - `METHOD_PIPELINE_MANAGER_CONTEXT` (the method-type id + persisted flag);
 * - the `pipelineEditorCascadeProviders` tier (label scope + cascade),
 *   reachable through the injector chain — the session tier's facades inject
 *   its `LabelEffects` / `LabelState` / `LabelConfigState`, and that tier also
 *   carries the shared `StagedPipelinesState` / `AssignedPipelinesState`
 *   ledgers this manager writes (owned by the outermost consumer so they
 *   survive a manager unmount and feed `consolidateMethodType`).
 */
export const methodPipelineManagerProviders: Provider[] = [
  ...pipelineStoreProviders,
  {
    provide: MANAGER_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: LIST_ASSIGNED_PIPELINES_QUERY,
    useFactory: (repo: MethodTypeRepository) => new ListAssignedPipelinesQuery(repo),
    deps: [MANAGER_METHOD_TYPE_REPOSITORY],
  },
  AssignedPipelinesEffects,
  MethodPipelineManagerFacade,
  ...pipelineEditorSessionProviders,
];
